import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const VisitsContext = createContext({
  visits: [],
  loading: false,
  error: null,
  createVisit: async () => {},
  cancelVisit: async () => {},
  refresh: async () => {},
});

export const useVisits = () => useContext(VisitsContext);

export function VisitsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const { data } = await api.get('/api/visits');
      if (mountedRef.current) setVisits(data.items || []);
    } catch (err) {
      if (mountedRef.current) setError('Failed to load visits');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated) {
      setVisits([]);
      return () => { mountedRef.current = false; };
    }
    refresh();
    return () => { mountedRef.current = false; };
  }, [isAuthenticated, refresh]);

  const createVisit = useCallback(async (payload) => {
    const { data } = await api.post('/api/visits', payload);
    if (mountedRef.current) {
      setVisits(prev => [data, ...prev]);
    }
    return data;
  }, []);

  const cancelVisit = useCallback(async (visitId) => {
    await api.delete(`/api/visits/${visitId}`);
    if (mountedRef.current) {
      setVisits(prev => prev.map(v =>
        v.id === visitId ? { ...v, status: 'cancelled' } : v
      ));
    }
  }, []);

  return (
    <VisitsContext.Provider value={{ visits, loading, error, createVisit, cancelVisit, refresh }}>
      {children}
    </VisitsContext.Provider>
  );
}

export default VisitsContext;
